/* Authenticated private previews. Object URLs belong to mounted views, never to persistence. */
(() => {
  const views = new Set();
  const queue = [];
  let active = 0;
  const maxActive = typeof matchMedia === 'function' && matchMedia('(max-width: 767px)').matches ? 1 : 2;
  const text = key => window.t('private_preview_' + key);
  for (const [lang, values] of Object.entries({
    es: { failed: 'No se pudo mostrar la foto. Tu archivo sigue guardado.', unsupported: 'Vista previa no disponible. Tu archivo está guardado.', retry: 'Volver a cargar vista previa' },
    en: { failed: 'Could not display the photo. Your file is still saved.', unsupported: 'Preview unavailable. Your file is saved.', retry: 'Reload preview' }
  })) {
    window.tacCopy ||= {}; window.tacCopy[lang] ||= {};
    for (const [key, value] of Object.entries(values)) window.tacCopy[lang]['private_preview_' + key] = value;
  }
  function pump() {
    while (active < maxActive && queue.length) {
      const job = queue.shift();
      if (!job.valid()) { job.cancel(); continue; }
      active++;
      job.run().finally(() => { active--; pump(); });
    }
  }
  function mount(root, img, slot) {
    let identity = '', generation = 0, controller, owned = '', pending = false, failed = false, retryable = true, failureKey = 'failed', disposed = false;
    const retry = document.createElement('button');
    retry.type = 'button'; retry.className = 'tac-private-preview-retry'; retry.textContent = text('retry'); retry.hidden = true;
    const notice = document.createElement('span'); notice.className = 'tac-private-preview-notice'; notice.setAttribute('role', 'status'); notice.hidden = true;
    img.after(notice, retry);
    const remoteKey = () => {
      const file = slot.remote(); const session = window.tacDraftClient?.session;
      return file?.status === 'verified' && session ? `${session.id}:${file.id || file.file_id}:${file.etag || ''}` : '';
    };
    function release() {
      generation++; controller?.abort(); controller = null; pending = false;
      if (owned) {
        if (slot.url() === owned) slot.setURL('');
        URL.revokeObjectURL(owned); owned = '';
      }
    }
    function paint() {
      const url = slot.url();
      if (url) { if (img.getAttribute('src') !== url) img.src = url; }
      else img.removeAttribute('src');
      img.hidden = !url;
      notice.textContent = failed ? text(failureKey) : '';
      notice.hidden = !failed;
      retry.hidden = !failed || !retryable;
    }
    function refresh() {
      if (disposed) return;
      if (!root.isConnected || !root.contains(img)) { dispose(); return; }
      const key = remoteKey();
      if (key !== identity) { release(); identity = key; failed = false; retryable = true; failureKey = 'failed'; }
      if (!key && !slot.url() && slot.localBlob?.()) {
        owned = URL.createObjectURL(slot.localBlob()); slot.setURL(owned);
      }
      paint();
      if (!key || slot.url() || pending || failed) return;
      pending = true;
      const version = generation;
      const file = slot.remote(); const session = window.tacDraftClient.session;
      const valid = () => !disposed && version === generation && key === remoteKey() &&
        !slot.url() && root.isConnected && root.contains(img);
      queue.push({ valid, cancel: () => { if (version === generation) pending = false; }, run: async () => {
        const requestController = new AbortController(); controller = requestController;
        const timeout = setTimeout(() => requestController.abort(), 30000);
        try {
          const response = await fetch(`${TAC_DRAFT_API}/v1/drafts/${encodeURIComponent(session.id)}/files/${encodeURIComponent(file.id || file.file_id)}/preview`, {
            headers: { Authorization: 'Bearer ' + session.token }, cache: 'no-store', credentials: 'omit',
            redirect: 'error', referrerPolicy: 'no-referrer', signal: controller.signal
          });
          if (!response.ok || response.headers.get('ETag') !== '"' + file.etag + '"') throw new Error('preview_unavailable');
          const blob = await response.blob();
          if (!valid()) return;
          // Reuse the existing serial lightweight decoder; originals are never re-uploaded.
          const light = await window.TACFileSlots.preview(blob);
          if (!valid()) return;
          if (!light) {
            const type = String(blob.type || '').toLowerCase();
            const error = new Error(type === 'image/heic' || type === 'image/heif' ? 'preview_format_unsupported' : 'preview_decode_failed');
            throw error;
          }
          owned = URL.createObjectURL(light); slot.setURL(owned); paint();
        } catch (error) {
          if (valid()) {
            failed = true;
            retryable = error?.message !== 'preview_format_unsupported';
            failureKey = retryable ? 'failed' : 'unsupported';
            paint();
          }
        } finally {
          clearTimeout(timeout);
          if (version === generation) { pending = false; controller = null; }
        }
      } });
      pump();
    }
    function dispose() {
      if (disposed) return;
      disposed = true; release();
      const local = slot.url();
      if (local?.startsWith('blob:')) { URL.revokeObjectURL(local); slot.setURL(''); }
      slot.releaseLocal?.();
      img.removeAttribute('src'); notice.remove(); retry.remove(); views.delete(view);
    }
    retry.onclick = event => { event.stopPropagation(); failed = false; refresh(); };
    const view = { root, refresh, dispose, isActive: () => !disposed };
    views.add(view); refresh(); return view;
  }
  function unmount(root) { for (const view of [...views]) if (!root || root === view.root || root.contains(view.root)) view.dispose(); }
  window.addEventListener('pagehide', () => unmount());
  window.TACPreviews = { mount, unmount };
})();
